import React from "react"

import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import { SxProps, alpha, lighten, darken, useTheme } from "@mui/material/styles"

import { toSentenceCase } from "@services/modifyStrings"

import {
  BillStatus,
  EquipmentStatus,
  InvoiceStatus,
  JobPackingType,
} from "@gql/graphql"

import * as colors from "@constants/colors"

type PillColor = {
  bgColor: string
  fontColor: string
  borderColor?: string
}

type ThemeColors = {
  light: PillColor
  dark: PillColor
}

export const getPillColors = (): Record<string, ThemeColors> => ({
  outlined: {
    light: {
      bgColor: "rgba(0, 0, 0, 0)",
      fontColor: colors.neutral[600],
      borderColor: alpha(colors.neutral.black, 0.1), // 0.1 darker than divider
    },
    dark: {
      bgColor: "rgba(0, 0, 0, 0)",
      fontColor: colors.neutral[400],
      borderColor: alpha(colors.neutral.white, 0.1), // 0.1 lighter than divider
    },
  },
  green: {
    light: { bgColor: colors.green[100], fontColor: colors.green[700] },
    dark: { bgColor: colors.green[800], fontColor: colors.green[200] },
  },
  pink: {
    light: { bgColor: colors.pink[100], fontColor: colors.pink[700] },
    dark: { bgColor: colors.pink[800], fontColor: colors.pink[200] },
  },
  orange: {
    light: { bgColor: colors.orange[100], fontColor: colors.orange[700] },
    dark: { bgColor: colors.orange[800], fontColor: colors.orange[200] },
  },
  yellow: {
    light: { bgColor: colors.yellow[100], fontColor: colors.yellow[700] },
    dark: { bgColor: colors.yellow[800], fontColor: colors.yellow[200] },
  },
  purple: {
    light: { bgColor: colors.purple[100], fontColor: colors.purple[700] },
    dark: { bgColor: colors.purple[800], fontColor: colors.purple[200] },
  },
  purple2: {
    light: { bgColor: colors.fuchsia[100], fontColor: colors.fuchsia[700] },
    dark: { bgColor: colors.fuchsia[800], fontColor: colors.fuchsia[200] },
  },
  blue: {
    light: { bgColor: colors.blue[100], fontColor: colors.blue[700] },
    dark: { bgColor: colors.blue[800], fontColor: colors.blue[200] },
  },
  gray: {
    light: { bgColor: colors.neutral[100], fontColor: colors.neutral[700] },
    dark: { bgColor: colors.neutral[800], fontColor: colors.neutral[200] },
  },
  red: {
    light: { bgColor: colors.red[100], fontColor: colors.red[700] },
    dark: { bgColor: colors.red[800], fontColor: colors.red[200] },
  },
  white: {
    light: { bgColor: colors.neutral.white, fontColor: colors.neutral[700] },
    dark: { bgColor: colors.neutral[400], fontColor: colors.neutral[100] },
  },
  violet: {
    light: { bgColor: colors.violet[100], fontColor: colors.violet[700] },
    dark: { bgColor: colors.violet[800], fontColor: colors.violet[200] },
  },
})

// Helper function to get theme-aware colors
export const getPillColor = (color: string, isDarkMode: boolean): PillColor => {
  return (
    getPillColors()[color]?.[isDarkMode ? "dark" : "light"] ||
    getPillColors().gray[isDarkMode ? "dark" : "light"]
  )
}

const stateColors = (isDarkMode: boolean): Record<string, PillColor> => ({
  active: getPillColor("green", isDarkMode),
  pending: getPillColor("yellow", isDarkMode),
  suspended: getPillColor("orange", isDarkMode),

  // PO states
  awaiting_shipment: getPillColor("orange", isDarkMode),
  partially_received: getPillColor("yellow", isDarkMode),
  received: getPillColor("green", isDarkMode),

  // order states
  draft: getPillColor("purple", isDarkMode),
  unfulfilled: getPillColor("yellow", isDarkMode),
  partially_fulfilled: getPillColor("orange", isDarkMode),

  // payment states
  not_paid: getPillColor("orange", isDarkMode),
  partially_paid: getPillColor("yellow", isDarkMode),
  payment_pending: getPillColor("yellow", isDarkMode),
  overdue: getPillColor("red", isDarkMode),
  disputed: getPillColor("purple", isDarkMode),

  // item hierarchies
  parent: getPillColor("purple", isDarkMode),
  variant: getPillColor("blue", isDarkMode),
  single: getPillColor("yellow", isDarkMode),

  // inventory states
  depleted: getPillColor("purple", isDarkMode),
  quarantined: getPillColor("pink", isDarkMode),

  // inventory receiving types
  accepted: getPillColor("green", isDarkMode),
  rejected: getPillColor("red", isDarkMode),

  // item availability states
  unavailable: getPillColor("red", isDarkMode),
  fully_available: getPillColor("green", isDarkMode),
  partially_available: getPillColor("yellow", isDarkMode),

  // picking & packing states
  not_started: getPillColor("red", isDarkMode),
  fully_picked: getPillColor("green", isDarkMode),
  partially_picked: getPillColor("yellow", isDarkMode),
  fully_packed: getPillColor("green", isDarkMode),
  partially_packed: getPillColor("yellow", isDarkMode),

  // job status states
  // not_started (already exists)
  in_progress: getPillColor("yellow", isDarkMode),
  completed: getPillColor("green", isDarkMode),

  // job priority states
  rush: getPillColor("red", isDarkMode),
  normal: getPillColor("gray", isDarkMode),

  // nested inventory states
  nested: getPillColor("orange", isDarkMode),
  child: getPillColor("purple", isDarkMode),

  // jobs
  job_number: getPillColor("violet", isDarkMode),

  // po number
  po_number: getPillColor("violet", isDarkMode),

  // quality check states
  pass: getPillColor("green", isDarkMode),
  fail: getPillColor("red", isDarkMode),

  // shipment states
  awaiting_shipment_creation: getPillColor("yellow", isDarkMode),
  awaiting_carrier: getPillColor("blue", isDarkMode),
  awaiting_pickup: getPillColor("pink", isDarkMode),
  awaiting_drop_off: getPillColor("orange", isDarkMode),

  // custom label types
  standard: getPillColor("blue", isDarkMode),
  custom: getPillColor("orange", isDarkMode),

  // quote states
  sent: getPillColor("orange", isDarkMode),
  revisionsNeeded: getPillColor("yellow", isDarkMode),
  expired: getPillColor("red", isDarkMode),
  closedWon: getPillColor("green", isDarkMode),
  closedLost: getPillColor("gray", isDarkMode),

  [JobPackingType.NoPacking.toLowerCase()]: getPillColor("gray", isDarkMode),
  [JobPackingType.StandardPacking.toLowerCase()]: getPillColor(
    "purple",
    isDarkMode,
  ),
  [JobPackingType.NestedPacking.toLowerCase()]: getPillColor(
    "orange",
    isDarkMode,
  ),

  // company kinds
  individual: getPillColor("violet", isDarkMode),
  company: getPillColor("blue", isDarkMode),

  // customer types
  customer: getPillColor("gray", isDarkMode),
  vendor: getPillColor("purple", isDarkMode),

  // invoice states
  [InvoiceStatus.Invoiced.toLowerCase()]: getPillColor("green", isDarkMode),
  [InvoiceStatus.InvoicePending.toLowerCase()]: getPillColor(
    "yellow",
    isDarkMode,
  ),
  [InvoiceStatus.NotInvoiced.toLowerCase()]: getPillColor("red", isDarkMode),
  [InvoiceStatus.PartiallyInvoiced.toLowerCase()]: getPillColor(
    "purple",
    isDarkMode,
  ),

  // bill states
  [BillStatus.Billed.toLowerCase()]: getPillColor("green", isDarkMode),
  [BillStatus.BillPending.toLowerCase()]: getPillColor("yellow", isDarkMode),
  [BillStatus.NotBilled.toLowerCase()]: getPillColor("red", isDarkMode),
  [BillStatus.PartiallyBilled.toLowerCase()]: getPillColor(
    "purple",
    isDarkMode,
  ),

  // equipment states
  [EquipmentStatus.Retired.toLowerCase()]: getPillColor("gray", isDarkMode),
  [EquipmentStatus.Inactive.toLowerCase()]: getPillColor("orange", isDarkMode),
  [EquipmentStatus.Maintenance.toLowerCase()]: getPillColor(
    "yellow",
    isDarkMode,
  ),

  // BOM item level
  assembly: getPillColor("blue", isDarkMode),
  component: getPillColor("orange", isDarkMode),
})

interface PillProps {
  status: string

  color?: string
  dataCy?: string
  endAdornment?: React.ReactNode
  icon?: React.ReactNode
  sx?: SxProps
  sxText?: SxProps
  label?: string | React.ReactNode
  onClick?: (event: React.MouseEvent<never>) => void
}

const Pill: React.FC<PillProps> = ({
  color,
  dataCy,
  endAdornment,
  icon,
  label,
  onClick,
  status,
  sx,
  sxText,
}: PillProps) => {
  const theme = useTheme()
  const isDarkMode = theme.palette.mode === "dark"

  let finalLabel = label
  const key = typeof status === "string" ? status?.toLowerCase() : "unknown"
  let stateColor = stateColors(isDarkMode)[key]

  if (stateColor === undefined) {
    stateColor = getPillColor("gray", isDarkMode)
  }

  // if color is passed in override them all
  if (color) {
    stateColor = getPillColor(color, isDarkMode)
  }

  if (label && typeof label === "string") {
    finalLabel = toSentenceCase(label)
  } else if (!label && typeof status === "string") {
    finalLabel = toSentenceCase(status)
  }

  return (
    <Box
      data-testid="pill"
      sx={{
        alignItems: "center",
        backgroundColor: stateColor?.bgColor,
        borderColor:
          stateColor?.borderColor ||
          (isDarkMode
            ? lighten(stateColor?.bgColor, 0.05)
            : darken(stateColor?.bgColor, 0.05)),
        borderRadius: `${theme.shape.borderRadius * 0.5}px`,
        borderStyle: "solid",
        borderWidth: "1px",
        color: stateColor?.fontColor,
        display: "inline-flex",
        flexDirection: "row",
        gap: theme.spacing(0.25),
        height: theme.spacing(2.5),
        justifyContent: "center",
        lineHeight: theme.spacing(2.5),
        margin: 0,
        maxWidth: "100%",
        overflow: "clip",
        padding: theme.spacing(0.25, 0.5),
        textAlign: "center",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "fit-content",
        ...(!!onClick && { cursor: "pointer" }),
        ...sx,
      }}
      onClick={onClick}
    >
      <Typography
        data-cy={dataCy || "status"}
        variant="overline"
        sx={{
          alignItems: "center",
          display: "inline-flex",
          fontWeight: 500,
          gap: theme.spacing(0.25),
          height: theme.spacing(2),
          lineHeight: theme.spacing(2),
          margin: 0,
          "& > svg": {
            width: theme.spacing(1.5),
            height: theme.spacing(1.5),
          },
          ...sxText,
        }}
      >
        {icon}
        {finalLabel}
        {endAdornment}
      </Typography>
    </Box>
  )
}

export default Pill
